export function compressImage(file, maxSizeKB = 400, quality = 0.85) {
    return new Promise((resolve, reject) => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d', { 
            alpha: false,
            willReadFrequently: false 
        });
        const img = new Image();
        
        img.onload = () => {
            let { width, height } = img;
            const originalSize = file.size / 1024;
            
            // Optimized dimension scaling - more granular and quality-focused
            let maxDimension;
            if (originalSize > 3000) {
                maxDimension = 800;
            } else if (originalSize > 2000) {
                maxDimension = 1000;
            } else if (originalSize > 1000) {
                maxDimension = 1200;
            } else if (originalSize > 500) {
                maxDimension = 1400;
            } else if (originalSize > 200) {
                maxDimension = 1600;
            } else {
                maxDimension = 1800;
            }
            
            // Calculate aspect ratio preserving dimensions
            const aspectRatio = width / height;
            if (width > height && width > maxDimension) {
                width = maxDimension;
                height = Math.round(width / aspectRatio);
            } else if (height > maxDimension) {
                height = maxDimension;
                width = Math.round(height * aspectRatio);
            }
            
            canvas.width = width;
            canvas.height = height;
            
            // High-quality image rendering
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';
            ctx.drawImage(img, 0, 0, width, height);
            
            const compressAndCheck = (currentQuality, attempt = 1) => {
                // More aggressive dimension reduction only for very large files
                if (attempt > 4 && originalSize > 2500 && currentQuality < 0.25) {
                    const scaleFactor = 0.85;
                    const newWidth = Math.max(Math.round(width * scaleFactor), 400);
                    const newHeight = Math.max(Math.round(height * scaleFactor), 300);
                    canvas.width = newWidth;
                    canvas.height = newHeight;
                    ctx.imageSmoothingEnabled = true;
                    ctx.imageSmoothingQuality = 'high';
                    ctx.drawImage(img, 0, 0, newWidth, newHeight);
                }
                
                canvas.toBlob((blob) => {
                    if (!blob) {
                        reject(new Error('Failed to compress image'));
                        return;
                    }
                    
                    const sizeKB = blob.size / 1024;
                    
                    // Stop when target reached or quality floor hit
                    if (sizeKB <= maxSizeKB || currentQuality <= 0.1) {
                        const compressedFile = new File([blob], file.name, {
                            type: blob.type,
                            lastModified: Date.now()
                        });
                        
                        console.log(
                            `Original: ${originalSize.toFixed(2)}KB → Compressed: ${sizeKB.toFixed(2)}KB ` +
                            `(${((1 - sizeKB/originalSize) * 100).toFixed(1)}% reduction, Quality: ${(currentQuality * 100).toFixed(0)}%)`
                        );
                        resolve(compressedFile);
                    } else {
                        // Gentler quality reduction curve for better quality preservation
                        const reductionFactor = sizeKB > maxSizeKB * 1.5 ? 0.7 : 0.8;
                        const newQuality = Math.max(currentQuality * reductionFactor, 0.1);
                        compressAndCheck(newQuality, attempt + 1);
                    }
                }, file.type, currentQuality);
            };
            
            // Start with higher quality for better preservation
            const initialQuality = originalSize > 2000 ? 0.7 : 
                                  originalSize > 1000 ? 0.75 : 
                                  quality;
            compressAndCheck(initialQuality);
        };
        
        img.onerror = () => {
            reject(new Error('Failed to load image'));
        };
        
        const reader = new FileReader();
        reader.onload = (e) => {
            img.src = e.target.result;
        };
        reader.onerror = () => {
            reject(new Error('Failed to read file'));
        };
        reader.readAsDataURL(file);
    });
}

export async function compressImages(files, maxSizeKB = 400) {
    const compressedFiles = [];
    const errors = [];
    
    // Process images in parallel for better performance
    const promises = files.map(async (file, index) => {
        try {
            const compressedFile = await compressImage(file, maxSizeKB);
            return { index, file: compressedFile, success: true };
        } catch (error) {
            errors.push({ file: file.name, error: error.message });
            return { index, file, success: false };
        }
    });
    
    const results = await Promise.all(promises);
    
    // Restore original order
    results.sort((a, b) => a.index - b.index);
    results.forEach(result => compressedFiles.push(result.file));
    
    if (errors.length > 0) {
        console.warn('Compression errors:', errors);
    }
    
    return compressedFiles;
}